/**
 * ParentStatusBar — sticky strip at the top of the parent dashboard
 * showing sessions remaining + next scheduled session + pack expiry alert.
 *
 * Renders nothing if the user has no packs AND no upcoming session — that
 * way it doesn't take up real estate for fresh-signup users who haven't
 * bought a pack yet.
 *
 * Visible on:
 *   - /dashboard (parent home)
 *   - /dashboard/billing
 *   - /dashboard/* (any parent-scoped view)
 *
 * Hidden for:
 *   - athlete-mode kids (they don't see billing data)
 *   - coach/owner users
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Ticket, Calendar, AlertTriangle, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentStatusBar() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Only show for parents (not coach/owner, not athlete-mode kids viewing their own page).
        if (!user) return;
        if (user.role === "coach" || user.role === "owner") return;
        if (user.athlete_mode_for) return;

        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/me/status-strip`, { withCredentials: true });
                if (!cancelled) setData(r.data);
            } catch (e) {
                // Silent fail — strip is non-critical.
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    if (loading || !data) return null;
    if (!user || user.role === "coach" || user.role === "owner") return null;
    if (user.athlete_mode_for) return null;

    const hasNothing = (
        data.sessions_remaining_total === 0
        && !data.next_session
        && !data.has_dead_packs
    );
    if (hasNothing) return null;

    const expiryUrgent = data.next_expiry && data.next_expiry.days_left !== null && data.next_expiry.days_left <= 14;

    return (
        <div
            data-testid="parent-status-bar"
            className="sticky top-[64px] z-40 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800"
        >
            <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-4 overflow-x-auto">
                {/* Sessions remaining */}
                <div className="flex items-center gap-2 shrink-0" data-testid="status-sessions">
                    <Ticket className="w-3.5 h-3.5 text-brand-red shrink-0" />
                    <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400">Sessions</div>
                    <div className="text-sm text-white font-semibold">
                        {data.sessions_remaining_total}
                    </div>
                </div>

                <div className="h-4 w-px bg-zinc-800 shrink-0" />

                {/* Next session */}
                {data.next_session ? (
                    <div className="flex items-center gap-2 shrink-0" data-testid="status-next-session">
                        <Calendar className="w-3.5 h-3.5 text-brand-red shrink-0" />
                        <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400">Next</div>
                        <div className="text-sm text-white">
                            <span className="font-semibold">{data.next_session.weekday}</span>{" "}
                            <span className="text-zinc-300">{data.next_session.date_label}</span>{" "}
                            <span className="text-zinc-400">·</span>{" "}
                            <span className="text-zinc-200">{data.next_session.time_label}</span>
                            {data.next_session.athlete_name !== "You" && (
                                <span className="text-zinc-400 ml-1">· {data.next_session.athlete_name}</span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 shrink-0 text-zinc-500 text-xs">
                        <Calendar className="w-3.5 h-3.5 shrink-0" />
                        <span className="text-[10px] tracking-[0.18em] uppercase">No session scheduled</span>
                    </div>
                )}

                {/* Dead pack alert (urgent) */}
                {data.has_dead_packs && (
                    <>
                        <div className="h-4 w-px bg-zinc-800 shrink-0" />
                        <Link
                            to="/dashboard?tab=messages"
                            data-testid="status-dead-pack-alert"
                            className="flex items-center gap-2 shrink-0 px-3 py-1 rounded-full bg-red-950/70 border border-red-700 hover:bg-red-900/70 transition-colors"
                        >
                            <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                            <span className="text-[11px] text-red-100 font-semibold tracking-wide">
                                Expired pack — talk to your coach
                            </span>
                        </Link>
                    </>
                )}

                {/* Expiry countdown */}
                {data.next_expiry && data.next_expiry.days_left !== null && (
                    <>
                        <div className="h-4 w-px bg-zinc-800 shrink-0" />
                        <div
                            data-testid="status-next-expiry"
                            className={`flex items-center gap-2 shrink-0 ${
                                expiryUrgent ? "text-amber-300" : "text-zinc-400"
                            }`}
                        >
                            <Clock className="w-3.5 h-3.5 shrink-0" />
                            <span className="text-[10px] tracking-[0.18em] uppercase">Expires in</span>
                            <span className="text-sm font-semibold">
                                {data.next_expiry.days_left}d
                            </span>
                            {expiryUrgent && (
                                <Link
                                    to="/policy/membership"
                                    className="text-[10px] tracking-[0.15em] uppercase text-brand-red hover:underline ml-1"
                                >
                                    Options →
                                </Link>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
